import * as THREE from 'three';
import type { PointPickResult } from '../types';
import { PickHelper } from './PickHelper';

export interface BrushingEvent {
  brushedPoints: PointPickResult[];
}

export interface BrushToolProps {
  isEnabled: boolean;
  onBrushing: (brushingUpdate: BrushingEvent) => void;
  brushSize?: number;
}

const DEFAULT_BRUSH_SIZE = 5;

/**
 * BrushToolHelper implements brushing mode.
 */
export class BrushToolHelper {
  private domElement: HTMLCanvasElement;

  private camera: THREE.OrthographicCamera | THREE.PerspectiveCamera;

  private renderer: THREE.WebGLRenderer;

  private enableControls: (isEnabled: boolean) => void;

  private pickHelper: PickHelper | undefined;

  private brushSize: number;

  private onBrushing: (brushingUpdate: BrushingEvent) => void;

  public isActive: boolean;

  private isBrushing: boolean;

  constructor(
    domElement: HTMLCanvasElement,
    camera: THREE.OrthographicCamera | THREE.PerspectiveCamera,
    renderer: THREE.WebGLRenderer,
    enableControls: (isEnabled: boolean) => void,
    pickHelper: PickHelper | undefined
  ) {
    this.domElement = domElement;
    this.camera = camera;
    this.renderer = renderer;
    this.enableControls = enableControls;
    this.pickHelper = pickHelper;
    this.brushSize = DEFAULT_BRUSH_SIZE;
    this.onBrushing = () => {};
    this.isActive = false;
    this.isBrushing = false;
  }

  public activate = (onBrushing: (brushingUpdate: BrushingEvent) => void, brushSize?: number) => {
    this.onBrushing = onBrushing;
    this.brushSize = brushSize ?? DEFAULT_BRUSH_SIZE;
    if (!this.isActive) {
      this.domElement.addEventListener('pointerdown', this.onPointerDown);
      this.domElement.addEventListener('pointermove', this.onPointerMove);
      this.domElement.addEventListener('pointerup', this.onPointerCancel);
      this.domElement.addEventListener('pointerleave', this.onPointerCancel);
      this.enableControls(false);
      this.isActive = true;
    }
  };

  public deactivate = () => {
    this.onBrushing = () => {};
    if (this.isActive) {
      this.domElement.removeEventListener('pointerdown', this.onPointerDown);
      this.domElement.removeEventListener('pointermove', this.onPointerMove);
      this.domElement.removeEventListener('pointerup', this.onPointerCancel);
      this.domElement.removeEventListener('pointerleave', this.onPointerCancel);
      this.enableControls(true);
      this.isActive = false;
    }
  };

  public dispose = () => {
    this.deactivate();
  };

  private updatePosition = (event: PointerEvent) => {
    const { clientX, clientY } = event;
    const { camera, renderer, brushSize, pickHelper, onBrushing } = this;
    if (!pickHelper) return;

    const halfSize = Math.floor(brushSize / 2);
    const size = halfSize * 2;

    const brushedPoints = pickHelper.pickPoints(
      event,
      { x: clientX - halfSize, y: clientY - halfSize, width: size, height: size },
      camera,
      renderer
    );

    onBrushing({ brushedPoints });
  };

  private onPointerDown = (event: PointerEvent) => {
    if (event.button === 0) {
      this.isBrushing = true;
      this.updatePosition(event);
    }
  };

  private onPointerMove = (event: PointerEvent) => {
    if (this.isBrushing) {
      this.updatePosition(event);
    }
  };

  private onPointerCancel = () => {
    this.isBrushing = false;
  };
}

export default BrushToolHelper;
